import { Product } from '../../products/models';
import { BusinessSupplier, MappingSuggestion, MasterProduct } from '../../master-data/models';
import { Business } from '../../shared/models';

export interface StockOrderItem {
    order?: string; // stock order id

    name: string,
    supplierItemNumber: string | null,
    articleNumber: string | null,
    code: string | null,
    color: string | null,
    size: string | null,
    quantity: number | null,
    bookedQuantity: number | null,
    purchasePrice: number | null,
    salesPrice: number | null,
    desiredOverallQuantity: number | null,
    actualOverallQuantity: number | null,
    receivedQuantity: number | null,
    containerQuantity: number | null,
    itemPrice: number | null,
    article: string | null,
    actualGoods: number | null,
    localOrder?: string;
    deleted: boolean;
    itemIndex: number;
    product?: any;
}

export interface StockOrder {
    uuid?: string;
    createTime: string;
    bookingTime?: string;
    createdBy_id?: string; // user id
    number: string;
    supplier: string | BusinessSupplier; // supplier id
    target: string; // warehouse or org unit or location id
    description: string;
    deleted?: boolean;
    items: StockOrderItem[];
    location: number;
    selected?: boolean;
    business: Business | number;
}
export interface StockOrderRequest {
    createdBy_id?: string; // user id
    supplier: string; // supplier id
    target: string; // warehouse or org unit or location id
    description: string;
    deleted?: boolean;
    items: StockOrderItem[];
    location: number;
}


export class StockOrderImportItem {
    totalQuantity: number;
    reOrderCode: string;
    unitPrice: number;

    constructor(reOrderCode: string, totalQuantity: number, unitPrice: number) {
        this.reOrderCode = reOrderCode;
        this.totalQuantity = totalQuantity;
        this.unitPrice = unitPrice;
    }

}

export interface StockOrderItemDatum {
    itemIndex: string | number;
    serialNumber: string;
    reOrderCode: string;
    supplierProductName: string;
    caseQuantity: string | number;
    master_supplier_container_qty: string | number;
    casesDelivered: string | number;
    costPerCase: string | number;
    unitsDelivered: string | number;
    costPerUnit: string | number;
    lineTotal: string | number;
    product?: any;
    calculatedLineTotal?: string | number;
    supplier?: BusinessSupplier;
    error?: string;
    error_type?: string;
    suggestions?: MappingSuggestion[];
    supplier_product?: Product;
    supplier_master_product?: MasterProduct;
    reorderCode?: string;
    reOrderCodeOfRetailerToSend:boolean;
    product_edited_or_updated :boolean;
    headerContainsReOrderCode: boolean; 
    supplier_code:string;
    supplier_quantity:string;
    retailer_quantity:string;
    retailer_reorder_code:string;
}

export interface StockOrderProductsPayload {
    stockOrderItems: StockOrderItemDatum[];
    business_id: number | string;
    master_supplier_id: number | string;
}

export interface MatchInvoicePayload {
    stockOrderItems: StockOrderItemDatum[],
    business_id: number | string,
    korona_supplier_business_id: number | string;
}

export interface InvoicePDFUpload {
    file: File, korona_supplier_business_id: number | string, business_id: number | string;
}

export const initialStockOrderSummary = {
    itemCount: 0,
    unMappedProducts: 0,
    expectedTotal: '0.00',
    calculatedTotal: '0.00'
};

export const initSpecifiHeaders = {
    caseQuantity: false,
    casesDelivered: false,
    unitsDelivered: false,
    reOrderCode: false,
    serialNumber: false
};


// Universal Catalog Ordering (FR-04/FR-05): Data Team review queue row for a
// Product auto-created from a catalog-ordered stock order line item.
export enum CatalogOrderTempProductStatus {
    PENDING = 'PENDING',
    APPROVED = 'APPROVED',
    DUPLICATE = 'DUPLICATE',
}

export interface CatalogOrderTempProductMinProduct {
    uuid: string;
    number: string;
    name: string;
}

export interface CatalogOrderTempProductMinMasterProduct {
    id: number;
    name: string;
}

export interface CatalogOrderTempProduct {
    id: number;
    business: number;
    stock_order: string;
    stock_order_number: string | null;
    stock_order_item: number | null;
    product: CatalogOrderTempProductMinProduct | null;
    master_product: CatalogOrderTempProductMinMasterProduct | null;
    reorder_code: string | null;
    case_qty: number | null;
    status: CatalogOrderTempProductStatus;
    resolved_to_product: CatalogOrderTempProductMinProduct | null;
    created_at: string;
    updated_at: string;
    approved_date: string | null;
    can_undo: boolean;
}

export enum CatalogOrderCorrectionStatus {
    PENDING = 'PENDING',
    FIXED = 'FIXED',
}

export enum CatalogOrderCorrectionTaskStatus {
    PENDING = 'PENDING',
    FIXED = 'FIXED',
}

export interface CatalogOrderCorrectionTask {
    id: number;
    temp_product: number;
    stock_order_item: string | null;
    source_product: CatalogOrderTempProductMinProduct;
    replacement_product: CatalogOrderTempProductMinProduct;
    status: CatalogOrderCorrectionTaskStatus;
    fixed_at: string | null;
    created_at: string;
    updated_at: string;
}

export interface CatalogOrderCorrection {
    id: number;
    business: number;
    stock_order: string;
    stock_order_number: string | null;
    location_name: string | null;
    supplier_name: string | null;
    status: CatalogOrderCorrectionStatus;
    task_count: number;
    fixed_task_count: number;
    tasks: CatalogOrderCorrectionTask[];
    fixed_at: string | null;
    created_at: string;
    updated_at: string;
}

// Universal Catalog Ordering (FR-01): a single sellable reorder code / case
// quantity row from the master catalog (MCPI), browsed via the supplier
// linked to the business. Enrichment fields are populated only when a local
// Korona product is resolved for the selected location.
export interface SupplierCatalogItem {
    id: number;
    master_product_id: number | null;
    master_product_company_instance_id: number | null;
    name: string;
    reorder_code: string;
    case_qty: number | null;
    existing_product_uuid: string | null;
    current_stock: number | null;
    last_purchase_price: number | null;
    last_order_date: string | null;
}

export interface StockOrderEditDataItem {
    name: string | null;
    quantity: number | null;
    reorder_code: string | null;
    case_qty: number | null;
    product_uuid: string | null;
    master_product_id: number | null;
    master_product_company_instance_id: number | null;
    supplierItemNumber?: string | null;
    containerQuantity?: number | null;
}

export interface StockOrderEditData {
    uuid: string;
    number: string;
    description: string;
    bookingTime: string | null;
    supplier: {
        uuid: string | null;
        name: string | null;
    };
    location: {
        id: number;
        korona_org_unit: string;
        name: string | null;
    } | null;
    items: StockOrderEditDataItem[];
    has_in_flight_task: boolean;
}

// Universal Catalog Ordering (FR-02): status of the background task that
// resolves/creates local Products for catalog-ordered items and saves the
// order on Korona (booking is explicit).
export enum CatalogStockOrderTaskStatus {
    PENDING = 'PENDING',
    STARTED = 'STARTED',
    PROGRESS = 'PROGRESS',
    SUCCESS = 'SUCCESS',
    FAILURE = 'FAILURE',
    PARTIAL_SUCCESS = 'PARTIAL_SUCCESS',
}

export enum CatalogStockOrderTaskItemStatus {
    PENDING = 'PENDING',
    SUCCESS = 'SUCCESS',
    FAILURE = 'FAILURE',
}

export enum CatalogStockOrderTaskItemResolution {
    MATCHED_EXISTING = 'MATCHED_EXISTING',
    TEMP_CREATED = 'TEMP_CREATED',
}

export interface CatalogStockOrderTask {
    id: number;
    stock_order: string | null;
    stock_order_uuid: string | null;
    stock_order_number: string | null;
    location: number;
    location_name: string | null;
    supplier: string | null;
    supplier_name: string | null;
    business: number;
    task_id: string | null;
    status: CatalogStockOrderTaskStatus;
    progress_message: string | null;
    result_json: any;
    items_count: number;
    items_success_count: number;
    items_failure_count: number;
    created_at: string;
    updated_at: string;
}

export interface CatalogStockOrderTaskItem {
    id: number;
    task: number;
    master_product: number | null;
    master_product_name: string | null;
    reorder_code: string | null;
    case_qty: number | null;
    quantity: number | null;
    resolution: CatalogStockOrderTaskItemResolution | null;
    product: string | null;
    product_uuid: string | null;
    product_name: string | null;
    product_number: string | null;
    stock_order_item: number | null;
    stock_order_item_uuid: string | null;
    status: CatalogStockOrderTaskItemStatus;
    error_message: string | null;
    created_at: string;
    updated_at: string;
}

export const TEXTRACT_MODELS = [
    {
      'id': 'aws_textract',
      'label': "AWS Textract"
    },
    {
      'id': 'azure_textract',
      'label': "Azure Textract"
    }
  ]